import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Shield, CheckCircle2, ArrowRight, MessageCircle } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import RegistrationPopup from '../components/RegistrationPopup';
import { processSteps } from '../mockData';

const TrademarkRegistration = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const benefits = [
    'Exclusive rights to use your mark nationwide',
    'Legal presumption of ownership',
    'Ability to sue in federal court',
    'Use of ® registered symbol',
    'Deterrent to potential infringers',
    'Foundation for international registration',
    'Valuable business asset',
    'Protection against domain name hijacking'
  ];

  const packages = [
    {
      name: 'Basic',
      price: '$45',
      subtitle: 'Great for small businesses covering multiple product or service classes.',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ]
    },
    {
      name: 'Deluxe',
      price: '$145',
      subtitle: 'Great for small businesses covering multiple product or service classes.',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager'
      ]
    },
    {
      name: 'Premium',
      price: '$395',
      popular: true,
      subtitle: 'For brands needing extra support, including help with USPTO office actions.',
      features: [
        'Case Review (TM Expert)',
        'Case Filing',
        'Federal Search &amp; Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Trademark Monitoring (06 Months)',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
      ]
    },
    {
      name: 'Elite',
      price: '$595',
      subtitle: 'All-inclusive plan with monitoring, renewals, and full legal assistance.',
      features: [
        'Case Review & Filing (U.S Registered IP Attorney)',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Cease & Desist Letter',
        'Same Day Filing',
        'Trademark Monitoring (1 Year)',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
        '100% Approval Guarantee',
        '*(Govt. filing fee not included)'
      ]
    }
  ];
  
  const faqs = [
    {
      question: 'What can be trademarked?',
      answer: 'You can trademark names, logos, slogans, sounds, colors, and even product shapes that distinguish your goods or services from others in the marketplace.'
    },
    {
      question: 'How long does registration take?',
      answer: 'The typical trademark registration process takes 8-12 months from filing to approval, though this can vary based on application complexity and any office actions.'
    },
    {
      question: 'Do I need a trademark attorney?',
      answer: 'While not legally required, working with an experienced trademark attorney significantly increases your chances of approval and ensures proper protection of your brand.'
    },
    {
      question: 'What is the difference between TM and ®?',
      answer: 'TM indicates you are claiming rights to a mark, while ® indicates your mark is officially registered with the USPTO and provides enhanced legal protection.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <Shield className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Trademark Registration Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand identity with professional trademark registration. Secure nationwide exclusive rights to your business name, logo, or slogan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsRegistrationOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Register My Trademark <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" onClick={() => window.openLiveChat && window.openLiveChat()} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is Trademark Registration?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Trademark registration is the legal process of registering your brand name, logo, slogan, or other identifying marks with the United States Patent and Trademark Office (USPTO). This registration grants you exclusive rights to use that mark in connection with your goods or services throughout the United States.
                </p>
                <p>
                  A trademark serves as a badge of origin, identifying your products or services and distinguishing them from those of competitors. Registration provides legal protection against unauthorized use and helps build brand recognition and consumer trust.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1626148750586-df6e1b0bebf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBjb25zdWx0YXRpb258ZW58MHx8fGJsdWV8MTc2Mjg4NzExM3ww&ixlib=rb-4.1.0&q=85" 
                alt="Professional office consultation and business services"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect your brand and gain valuable legal rights
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our streamlined process makes registration simple and efficient
            </p>
          </div>
          <div className="max-w-4xl mx-auto relative">
            {/* Connecting Line */}
            <div className="absolute left-[60px] top-[80px] bottom-[80px] w-1 bg-gradient-to-b from-orange-600 via-orange-500 to-amber-600"></div>
            
            <div className="space-y-12">
              {processSteps.map((step) => (
                <div key={step.step} className="relative">
                  {/* Step Number Badge */}
                  <div className="absolute left-0 w-[120px] h-[120px]">
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl rotate-6 opacity-20"></div>
                      <div className="relative bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl w-full h-full flex items-center justify-center shadow-xl">
                        <span className="text-4xl font-bold text-white">{step.step}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Card */}
                  <div className="ml-[160px] bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-100 hover:border-orange-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
                      </div>
                      <div className="ml-6 flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="h-8 w-8 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose Your Package</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Flexible pricing options to fit your needs and budget
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => (
              <Card key={index} className={`border-2 ${pkg.popular ? 'border-orange-600 shadow-xl' : 'border-gray-200'} relative h-[580px] flex flex-col`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
                <CardContent className="pt-8 flex flex-col h-full">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-orange-600 mb-3">{pkg.price}</div>
                  <p className="text-sm text-gray-600 mb-4 h-[40px]">{pkg.subtitle}</p>
                  
                  <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '280px' }}>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-auto">
                    <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                    <Button onClick={() => setIsRegistrationOpen(true)} className={`w-full ${pkg.popular ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}>
                      Register My Trademark
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA Box */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-orange-600 to-amber-600 border-0 shadow-2xl h-[580px] flex flex-col">
              <CardContent className="pt-12 pb-12 px-8 flex flex-col justify-center items-center text-center h-full">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Need Help Choosing?
                </h3>
                <p className="text-lg md:text-xl text-orange-100 mb-8 max-w-2xl">
                  Our trademark experts are here to help you select the perfect package for your business needs. Get a free consultation today!
                </p>
                <div className="space-y-4 mb-8 text-left max-w-md">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                    <span className="text-white">Free expert consultation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                    <span className="text-white">Personalized package recommendation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                    <span className="text-white">No obligation, no pressure</span>
                  </div>
                </div>
                <Button 
                  size="lg"
                  onClick={() => window.openLiveChat && window.openLiveChat()}
                  className="bg-white text-orange-600 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 h-auto"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Talk to an Expert Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Protect your brand today with our professional trademark registration services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsRegistrationOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Register My Trademark <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" onClick={() => window.openLiveChat && window.openLiveChat()} variant="outline" className="border-white text-white hover:bg-white/10">
              <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
            </Button>
          </div>
        </div>
      </section>

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <RegistrationPopup isOpen={isRegistrationOpen} onClose={() => setIsRegistrationOpen(false)} />
    </div>
  );
};

export default TrademarkRegistration;
